'use client'

import { useState, useRef, useCallback } from 'react'

const API = process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:3001'

interface SearchResult {
  id:       string
  title:    string
  url:      string
  snippet:  string
  source:   string
  score?:   number
  type?:    string
}

interface SearchResponse {
  results:    SearchResult[]
  summary?:   string
  query:      string
  totalTime?: number
  sources?:   string[]
}

const SUGGESTIONS = [
  'Règlement de zonage Sainte-Adèle',
  'Saint-Eustache zones résidentielles',
  'Permis de construction Québec',
  'Taxe foncière municipale',
  'Marges de recul résidentiel',
]

export function HomeSearchCard() {
  const [query, setQuery]     = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult]   = useState<SearchResponse | null>(null)
  const [error, setError]     = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  const runSearch = useCallback(async (q: string) => {
    if (!q.trim()) return
    abortRef.current?.abort()
    abortRef.current = new AbortController()
    setLoading(true)
    setError(null)
    setResult(null)
    setExpanded(true)
    try {
      const url = `${API}/search?q=${encodeURIComponent(q)}&limit=6&sources=vector,web&rerank=true`
      const r = await fetch(url, {
        credentials: 'include',
        signal: abortRef.current.signal,
      })
      if (!r.ok) throw new Error(`${r.status}`)
      const data = await r.json()
      setResult(data)
    } catch (e: any) {
      if (e.name !== 'AbortError') setError('Recherche indisponible')
    } finally {
      setLoading(false)
    }
  }, [])

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') runSearch(query)
    if (e.key === 'Escape') { setExpanded(false); setResult(null) }
  }

  const sourceLabel = (s: string) => {
    if (s === 'vector' || s === 'qdrant')  return { label: 'Base locale', color: '#a78bfa' }
    if (s === 'web' || s === 'searxng')    return { label: 'Web', color: '#34d399' }
    if (s === 'elasticsearch' || s === 'es') return { label: 'ES', color: '#fbbf24' }
    return { label: s, color: 'var(--text-muted)' }
  }

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      {/* Search bar */}
      <div style={{
        display: 'flex', gap: 8, alignItems: 'center',
        background: 'var(--bg-card)',
        border: `1px solid ${expanded ? 'rgba(79,124,255,0.45)' : 'var(--border-dim)'}`,
        borderRadius: expanded ? '12px 12px 0 0' : 12,
        padding: '10px 14px',
        transition: 'border-color 0.15s, border-radius 0.15s',
        boxShadow: expanded ? '0 0 0 3px rgba(79,124,255,0.08)' : 'none',
      }}>
        <span style={{ fontSize: 16, color: 'var(--text-muted)', flexShrink: 0 }}>◎</span>
        <input
          ref={inputRef}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={onKey}
          onFocus={() => !result && setExpanded(true)}
          placeholder="Recherche dans la base légale, vectorielle et le web…"
          style={{
            flex: 1, background: 'none', border: 'none', outline: 'none',
            fontSize: 14, color: 'var(--white)', fontFamily: 'var(--font-sans)',
            caretColor: 'var(--accent)',
          }}
        />
        {loading && (
          <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--accent)', animation: 'pulse 1s infinite' }}>
            ⟳
          </span>
        )}
        {query && (
          <button onClick={() => runSearch(query)} style={{
            background: 'var(--accent)', border: 'none', borderRadius: 7, padding: '5px 12px',
            fontSize: 11, fontWeight: 600, color: '#fff', cursor: 'pointer', flexShrink: 0,
            fontFamily: 'var(--font-mono)', letterSpacing: '0.06em',
          }}>
            Lancer
          </button>
        )}
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid rgba(79,124,255,0.3)', borderTop: 'none',
          borderRadius: '0 0 12px 12px',
          overflow: 'hidden',
        }}>
          {/* Suggestions — shown when no results yet */}
          {!result && !loading && !error && (
            <div style={{ padding: '10px 14px 14px' }}>
              <div style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>
                Suggestions
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {SUGGESTIONS.map(s => (
                  <button key={s} onClick={() => { setQuery(s); runSearch(s) }} style={{
                    background: 'rgba(79,124,255,0.07)', border: '1px solid rgba(79,124,255,0.18)',
                    borderRadius: 6, padding: '4px 10px', fontSize: 11, color: 'var(--text-secondary)',
                    cursor: 'pointer', fontFamily: 'var(--font-sans)',
                  }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Loading skeleton */}
          {loading && (
            <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[1, 2, 3].map(i => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 5, opacity: 1 - i * 0.2 }}>
                  <div style={{ height: 12, width: `${75 - i * 10}%`, background: 'rgba(255,255,255,0.06)', borderRadius: 4 }} />
                  <div style={{ height: 9, width: '90%', background: 'rgba(255,255,255,0.04)', borderRadius: 4 }} />
                </div>
              ))}
              <div style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', marginTop: 4 }}>
                Base vectorielle · SearXNG · LLM enrichissement…
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ padding: '12px 18px', fontSize: 12, color: '#f87171', fontFamily: 'var(--font-mono)' }}>
              {error}
            </div>
          )}

          {/* Results */}
          {result && !loading && (
            <div style={{ padding: '12px 18px 16px' }}>
              {/* LLM summary */}
              {result.summary && (
                <div style={{
                  padding: '10px 14px', marginBottom: 12,
                  background: 'rgba(79,124,255,0.06)', border: '1px solid rgba(79,124,255,0.18)',
                  borderRadius: 8, borderLeft: '3px solid var(--accent)',
                }}>
                  <div style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--accent)', letterSpacing: '0.1em', marginBottom: 5 }}>
                    ◈ SYNTHÈSE IA
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0, fontFamily: 'var(--font-mono)' }}>
                    {result.summary}
                  </p>
                </div>
              )}

              {/* Sources badges */}
              {result.sources && result.sources.length > 0 && (
                <div style={{ display: 'flex', gap: 5, marginBottom: 10, alignItems: 'center' }}>
                  <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>Sources :</span>
                  {result.sources.map(s => {
                    const { label, color } = sourceLabel(s)
                    return (
                      <span key={s} style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color, background: `${color}18`, border: `1px solid ${color}30`, borderRadius: 4, padding: '1px 6px' }}>
                        {label}
                      </span>
                    )
                  })}
                  {result.totalTime && (
                    <span style={{ marginLeft: 'auto', fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                      {result.totalTime}ms
                    </span>
                  )}
                </div>
              )}

              {/* Result cards */}
              {result.results.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', padding: '8px 0' }}>
                  Aucun résultat pour « {result.query} »
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
                  {result.results.map(r => {
                    const { label, color } = sourceLabel(r.source ?? r.type ?? '')
                    return (
                      <a key={r.id ?? r.url} href={r.url} target="_blank" rel="noopener noreferrer"
                        style={{
                          display: 'flex', flexDirection: 'column', gap: 4, padding: '10px 12px',
                          background: 'rgba(255,255,255,0.025)', border: '1px solid var(--border-dim)',
                          borderRadius: 8, textDecoration: 'none', color: 'inherit',
                          transition: 'border-color 0.12s',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(79,124,255,0.35)')}
                        onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border-dim)')}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color, letterSpacing: '0.08em' }}>
                            {label}
                          </span>
                          {r.score != null && (
                            <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                              {(r.score * 100).toFixed(0)}%
                            </span>
                          )}
                        </div>
                        <div style={{
                          fontSize: 12, fontWeight: 600, color: 'var(--white)', lineHeight: 1.35,
                          overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        }}>
                          {r.title}
                        </div>
                        {r.snippet && (
                          <div style={{
                            fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5,
                            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          }}>
                            {r.snippet}
                          </div>
                        )}
                      </a>
                    )
                  })}
                </div>
              )}

              {/* Full search link */}
              <div style={{ marginTop: 10, textAlign: 'right' }}>
                <a href={`/search?q=${encodeURIComponent(result.query)}`}
                  style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--accent)', textDecoration: 'none' }}>
                  Voir tous les résultats dans Recherche →
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
